import React from 'react';

import { ButtonProps, Grid, TextFieldProps } from '@ha/dsl';

import DateRangePickerInputsWrapper from './DateRangePickerInputsWrapper';
import Calendar from '../Calendar/Calendar';
import CalendarFooter from '../Calendar/CalendarFooter';
import CalendarInfo from '../Calendar/CalendarInfo';
import {
  DatePickerInputsProps,
  DateRangePickerProps,
  FOCUSED_FIELD,
  OverlayProps,
  DayPickerProps,
  RangeModifier,
  TranslateFunc,
  FormatDate,
} from '../types';

interface Props {
  AdditionalInfo?: React.ReactNode;
  autoClose: boolean;
  buttonProps: ButtonProps;
  noEndDate: boolean;
  noEndDateLabel: string;
  CaptionSubtitle?: DateRangePickerProps['CaptionSubtitle'];
  DatePickerInputs: React.ComponentType<DatePickerInputsProps>;
  datePickerProps: DayPickerProps;
  focused: FOCUSED_FIELD | null;
  from?: Date;
  fromEmptyValueLabel: string;
  to?: Date;
  onDatesClear?: () => void;
  onNoEndDate: () => void;
  onOverlayExit: () => void;
  onOverlayOpen: (field: FOCUSED_FIELD) => void;
  overlayCloseButton: string;
  clearDatesButton?: string;
  fromInputProps: TextFieldProps;
  minimumPeriod?: number;
  moveInWindows: RangeModifier[];
  moveInWindowsTooltip: string;
  toInputProps: TextFieldProps;
  toEmptyValueLabel: string;
  Overlay: React.FC<OverlayProps>;
  header?: React.ReactNode;
  T: TranslateFunc;
  formatDate: FormatDate;
}

const DateRangePickerOverlay: React.FC<Props> = ({
  AdditionalInfo,
  autoClose,
  buttonProps,
  noEndDate,
  noEndDateLabel,
  CaptionSubtitle,
  DatePickerInputs,
  focused,
  from,
  to,
  onDatesClear,
  onNoEndDate,
  onOverlayExit,
  onOverlayOpen,
  Overlay,
  overlayCloseButton,
  clearDatesButton,
  fromInputProps,
  toInputProps,
  fromEmptyValueLabel,
  toEmptyValueLabel,
  datePickerProps,
  minimumPeriod,
  moveInWindows,
  moveInWindowsTooltip,
  header,
  T,
  formatDate,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (open && !focused && autoClose) {
      setOpen(false);
    }
  }, [focused, open]);

  const onOverlayOpenHandler = React.useCallback(
    (field: FOCUSED_FIELD) => {
      setOpen(true);
      onOverlayOpen(field);
    },
    [onOverlayOpen],
  );
  const onOverlayCloseHandler = React.useCallback(() => {
    setOpen(false);
  }, []);
  const onNoEndDateHandler = React.useCallback(() => {
    onNoEndDate();
    setOpen(false);
  }, [onNoEndDate]);

  return (
    <Grid container justify="center" ref={ref}>
      <DateRangePickerInputsWrapper
        focused={focused}
        from={from}
        fromEmptyValueLabel={fromEmptyValueLabel}
        fromInputProps={fromInputProps}
        onFieldFocusChange={onOverlayOpenHandler}
        to={to}
        toEmptyValueLabel={toEmptyValueLabel}
        toInputProps={toInputProps}
        formatDate={formatDate}
      >
        {inputsProps => <DatePickerInputs {...inputsProps} />}
      </DateRangePickerInputsWrapper>
      <Overlay
        containerRef={ref}
        focused={focused}
        from={from}
        onFieldFocusChange={onOverlayOpen}
        onOverlayExit={onOverlayExit}
        onOverlayClose={onOverlayCloseHandler}
        open={open}
        OverlayTitle={header}
        OverlayActions={
          <CalendarFooter
            buttonProps={buttonProps}
            clearDatesButton={clearDatesButton}
            onClearDates={onDatesClear}
            onNoEndDate={onNoEndDateHandler}
            onOverlayClose={onOverlayCloseHandler}
            showOverlayCloseButton={!autoClose}
            showNoEndDateButton={focused === FOCUSED_FIELD.TO && noEndDate}
            overlayCloseButton={overlayCloseButton}
            noEndDateButton={noEndDateLabel}
          />
        }
        OverlayInfo={
          <CalendarInfo
            additionalInfo={AdditionalInfo}
            minimumPeriod={minimumPeriod}
            hasMoveInWindowHint={moveInWindows.length > 0}
            T={T}
          />
        }
        to={to}
        formatDate={formatDate}
        T={T}
      >
        <Calendar
          {...datePickerProps}
          captionSubtitle={CaptionSubtitle}
          highlightMoveInWindows={focused === FOCUSED_FIELD.FROM}
          moveInWindows={moveInWindows}
          moveInWindowsTooltip={moveInWindowsTooltip}
          usePrevMonth={focused === null && autoClose}
        />
      </Overlay>
    </Grid>
  );
};

export default DateRangePickerOverlay;
